import React, { useState, useEffect } from 'react';

import
{
  Icon,
  Button,
  Input,
  Text,
  Radio,
  FormAlert,
} from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

import { LinkTapSiteModalProps, LinkTapSiteFormData } from './LinkTapSiteModal.type';
import { DataModelApiService } from '../../services/dataModelApi';
import { getPlatformDisplayName } from '../../config/platformConfig';

// Data Model Migration Section Component
interface DataModelMigrationSectionProps {
  projectId: string;
  hasDataModels: boolean;
  onMigrationComplete: () => void;
  onMigrationChoice: (choice: boolean | undefined) => void;
  isProjectLinked: boolean;
  shouldExecuteMigration?: boolean;
  platformType?: string;
}

const DataModelMigrationSection: React.FC<DataModelMigrationSectionProps> = ({
  projectId,
  hasDataModels,
  onMigrationComplete,
  onMigrationChoice,
  isProjectLinked,
  shouldExecuteMigration = false,
  platformType = 'TAP',
}) => {
  const [isUserWantToMigrate, setIsUserWantToMigrate] = useState<boolean | undefined>();
  const [finalizationProgress, setFinalizationProgress] = useState(false);
  const [removeModelProgress, setRemoveModelProgress] = useState(false);
  const [error, setError] = useState('');

  if (!hasDataModels) return null;

  useEffect(() => {
    onMigrationChoice(isUserWantToMigrate);
  }, [isUserWantToMigrate, onMigrationChoice]);

  // Only execute migration when explicitly told to execute (after TAP site is linked)
  useEffect(() => {
    // If no choice made yet, don't do anything
    if (isUserWantToMigrate === undefined) {
      return;
    }

    // Only execute migration when shouldExecuteMigration is true
    // This ensures migration happens AFTER TAP site is linked/updated
    if (!shouldExecuteMigration) {
      // Don't execute migration yet, just store the choice
      return;
    }

    if (isUserWantToMigrate === true) {
      handleFinalizeModel();
    } else if (isUserWantToMigrate === false) {
      handleRemoveModel();
    }
  }, [isUserWantToMigrate, shouldExecuteMigration]);

  const handleFinalizeModel = async () => {
    setFinalizationProgress(true);
    setError('');

    try {
      await new Promise<void>((resolve, reject) => {
        DataModelApiService.finalizeModel(projectId, {
          onAction: (message: string) => {
            console.log(message);
          },
          onComplete: async (success: boolean, message: string) => {
            if (success) {
              resolve();
            } else {
              reject(new Error('Failed to finalize data model. Please try again.'));
            }
          },
          onError: (error: Error) => {
            reject(error);
          },
        });
      });
      handleRemoveModel();
      setFinalizationProgress(false);
      onMigrationComplete();
    } catch (err: any) {
      console.error('Failed to finalize data model:', err);
      setError('Failed to finalize data model');
      setFinalizationProgress(false);
      setIsUserWantToMigrate(undefined);
    }
  };

  const handleRemoveModel = async () => {
    setRemoveModelProgress(true);
    setError('');

    try {
      const result = await DataModelApiService.saveDataModel(projectId, []);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to remove data model');
      }
      
      setRemoveModelProgress(false);
      onMigrationComplete();
    } catch (err: any) {
      setError(err.message || 'Failed to remove data model');
      setRemoveModelProgress(false);
      setIsUserWantToMigrate(undefined);
    }
  };

  return (
    <div className="flex flex-col gap-bg-v2-lg w-full bg-bg-secondary rounded-bg-v2-lg p-bg-v2-lg border border-border-secondary">
      {/* Header Section */}
      <div className="flex items-start gap-bg-v2-md">
        <div className="flex-1 space-y-bg-v2-sm">
          <Text variant="text-xs" color="secondary">
            You have data model objects created in Prototype mode. 
            Please choose whether to migrate them to Build mode.
          </Text>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <FormAlert
          message={error}
          variant="error"
          onDismiss={() => setError('')}
        />
      )}

      {/* Migration Options */}
      <div className="flex flex-col gap-bg-v2-sm">
        <Text variant="text-xs" weight="medium" color="secondary" className="uppercase tracking-wide">
          Migration Options
        </Text>
        
        <div className="flex flex-col gap-bg-v2-md">
          {/* Yes Option */}
          <label className={`flex gap-bg-v2-md items-start p-bg-v2-md rounded-bg-v2-md border border-border-secondary transition-all ${finalizationProgress || removeModelProgress ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-bg-tertiary'}`}>
            <Radio
              name="migration"
              checked={isUserWantToMigrate === true}
              onChange={() => setIsUserWantToMigrate(true)}
              disabled={finalizationProgress || removeModelProgress}
              size="sm"
            />
            <div className="flex-1 space-y-bg-v2-xs">
              <div className="flex items-center gap-bg-v2-xs">
                <Text variant="text-xs" weight="semibold" color="primary">
                  Yes, migrate to Build mode {finalizationProgress && <Icon name={IconName.Loading} size="sm" variant="secondary" className="animate-spin" />}
                </Text>
              </div>
              {!isProjectLinked && (
                <Text variant="text-xs" color="secondary" className="italic">
                  Will be migrated after linking {getPlatformDisplayName(platformType as any)} site
                </Text>
              )}
            </div>
          </label>

          {/* No Option */}
          <label className={`flex gap-bg-v2-md items-start p-bg-v2-md rounded-bg-v2-md border border-border-secondary transition-all ${finalizationProgress || removeModelProgress ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-bg-tertiary'}`}>
            <Radio
              name="migration"
              checked={isUserWantToMigrate === false}
              onChange={() => setIsUserWantToMigrate(false)}
              disabled={finalizationProgress || removeModelProgress}
              size="sm"
            />
            <div className="flex-1 space-y-bg-v2-xs">
              <div className="flex items-center gap-bg-v2-xs">
                <Text variant="text-xs" weight="semibold" color="primary">
                  No, remove data models {removeModelProgress && <Icon name={IconName.Loading} size="sm" variant="secondary" className="animate-spin" />}
                </Text>
              </div>
              {!isProjectLinked && (
                <Text variant="text-xs" color="secondary" className="italic">
                  Will be removed after linking {getPlatformDisplayName(platformType as any)} site
                </Text>
              )}
            </div>
          </label>
        </div>
      </div>
    </div>
  );
};

export const LinkTapSiteModal: React.FC<LinkTapSiteModalProps> = ({ 
  isOpen,
  onClose,
  onSubmit,
  initialFormData,
  projectId,
  platformType,
}) => {
  const [formData, setFormData] = useState<LinkTapSiteFormData>(initialFormData || {});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [migrationChoice, setMigrationChoice] = useState<boolean | undefined>();
  const [isMigrationComplete, setIsMigrationComplete] = useState(false);
  const [shouldExecuteMigration, setShouldExecuteMigration] = useState(false);
  const [hasDataModels, setHasDataModels] = useState(false);
  const [checkingDataModels, setCheckingDataModels] = useState(false);
  
  const isServiceNow = platformType === 'ServiceNow';
  const isPowerApps = platformType === 'PowerApps';
  const isSalesforce = platformType === 'Salesforce';

  // Check for data model existence when modal opens
  useEffect(() => {
    const checkDataModels = async () => {
      if (isOpen && projectId) {
        setCheckingDataModels(true);
        try {
          const result = await DataModelApiService.getDataModelPreview(projectId);
          setHasDataModels(result.exists);
        } catch (error) {
          console.error('Failed to check data models:', error);
          setHasDataModels(false);
        } finally {
          setCheckingDataModels(false);
        }
      }
    };

    checkDataModels();
  }, [isOpen, projectId]);

  // Sync formData with initialFormData when modal opens or initialFormData changes
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData || {});
      setMigrationChoice(undefined);
      setIsMigrationComplete(false);
      setError('');
      setShouldExecuteMigration(false);
    }
  }, [isOpen, initialFormData]);

  const isUpdating = initialFormData.tapUrl !== '';
  const isProjectLinked = isUpdating; // Project is linked if we're updating

  const clearInputFields = () => setFormData({});

  const validateForm = (): boolean => {
    if (isServiceNow) {
      if (!formData.tapUrl || !formData.servicenowUsername || !formData.servicenowPassword) {
        setError('All fields are required');
        return false;
      }
    } else if (isPowerApps) {
      if (!formData.tapUrl || !formData.powerappsTenantId || 
          !formData.powerappsClientId || !formData.powerappsClientSecret) {
        setError('All fields are required');
        return false;
      }
    } else if (isSalesforce) {
      if (!formData.tapUrl || !formData.salesforceClientId || !formData.salesforceClientSecret) {
        setError('All fields are required');
        return false;
      }
    } else {
      if (!formData.tapUrl || !formData.clientId || !formData.clientSecret) {
        setError('All fields are required');
        return false;
      }
    }
    
    if (!isUpdating && hasDataModels && migrationChoice === undefined) {
      setError('Please select a migration option');
      return false;
    }
    
    return true;
  };

  if (!isOpen) return null;

  const handleMigrationComplete = () => {
    setIsMigrationComplete(true);
    setLoading(false);
    // Close modal after migration completes
    setTimeout(() => {
      onClose();
      setSuccess(false);
      clearInputFields();
    }, 1000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Prepare form data with migration choice
      const submissionData = {
        ...formData,
        migrateDataModel: migrationChoice === true // Only true if user explicitly selected "Yes"
      };
      
      // Submit to backend (TAP linking + migration happens in one call)
      await onSubmit(submissionData);
      
      setSuccess(true);
      setLoading(false);
      
      // Close modal after success
      setTimeout(() => {
        onClose();
        setSuccess(false);
        clearInputFields();
      }, 2000);
      
    } catch (err: any) {
      const platformName = getPlatformDisplayName(platformType as any);
      setError(err.response?.data?.detail || `Failed to ${isUpdating ? 'update' : 'link'} ${platformName} site. Please try again.`);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    if (!loading) {
      setError('');
      clearInputFields();
      setSuccess(false);
      onClose();
    }
    setIsClosing(false);
  };

  const clearError = () => {
    if (error) setError('');
  };

  const isLinkButtonDisabled = () => {
    if (loading || success) return true;
    
    if (isServiceNow) {
      if (!formData.tapUrl || !formData.servicenowUsername || !formData.servicenowPassword) return true;
    } else if (isPowerApps) {
      if (!formData.tapUrl || !formData.powerappsTenantId || 
          !formData.powerappsClientId || !formData.powerappsClientSecret) return true;
    } else if (isSalesforce) {
      if (!formData.tapUrl || !formData.salesforceClientId || !formData.salesforceClientSecret) return true;
    } else {
      if (!formData.tapUrl || !formData.clientId || !formData.clientSecret) return true;
    }
    
    if (!isUpdating && hasDataModels && migrationChoice === undefined) return true;
    
    return false;
  };

  return (
    <div 
      className={`fixed overflow-y-auto scrollbar-hide inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[400px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Top Section */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <div>
            <Text variant="text-xl" weight="bold">
          {isServiceNow 
            ? (isUpdating ? 'Update ServiceNow Site' : 'Link ServiceNow Site')
            : isPowerApps
            ? (isUpdating ? 'Update PowerApps Site' : 'Link PowerApps Site')
            : isSalesforce
            ? (isUpdating ? 'Update Salesforce Site' : 'Link Salesforce Site')
            : (isUpdating ? 'Update TAP Site' : 'Link TAP Site')
          }
            </Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            disabled={loading}
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-bg-v2-md items-center px-bg-v2-3xl py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

      {/* Error Message */}
      {error && (
        <div className="px-bg-v2-3xl pb-bg-v2-md">
          <FormAlert
            message={error}
            variant="error"
            onDismiss={() => setError('')}
          />
        </div>
        )}
        {/* Migration Section - Show only when linking for the first time (not updating) */}
        {!isUpdating && !checkingDataModels && hasDataModels && !isMigrationComplete && (
          <div className='px-bg-v2-3xl py-bg-v2-xl'>
            <DataModelMigrationSection 
              projectId={projectId} 
              hasDataModels={hasDataModels}
              onMigrationComplete={handleMigrationComplete}
              onMigrationChoice={setMigrationChoice}
              isProjectLinked={isProjectLinked}
              shouldExecuteMigration={shouldExecuteMigration}
              platformType={platformType}
            />
          </div>
        )}

        {/* Middle Section */}
        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="flex flex-col items-center gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto">
            <div className="w-full space-y-6">
              {isServiceNow ? (
                <>
                  <Input
                    label="ServiceNow URL"
                    type="text"
                    id="serviceNowUrl"
                    value={formData.tapUrl ?? initialFormData.tapUrl}
                    onChange={(e) => {
                      setFormData({ ...formData, tapUrl: e.target.value });
                      clearError();
                    }}
                    placeholder="Enter ServiceNow URL"
                    required
                    disabled={loading || success}
                  />
                  <Input
                    label="Username"
                    type="text"
                    id="username"
                    value={formData.servicenowUsername ?? initialFormData.servicenowUsername}
                    onChange={(e) => {
                      setFormData({ ...formData, servicenowUsername: e.target.value });
                      clearError();
                    }}
                    placeholder="Enter username"
                    required
                    disabled={loading || success}
                    autoComplete="off"
                  />
                  <Input
                    label="Password"
                    type="password"
                    id="password"
                    value={formData.servicenowPassword ?? initialFormData.servicenowPassword}
                    onChange={(e) => {
                      setFormData({ ...formData, servicenowPassword: e.target.value });
                      clearError();
                    }}
                    placeholder="Enter password"
                    required
                    disabled={loading || success}
                    autoComplete="off"
                  />
                </>
              ) : isPowerApps ? (
                <>
                  <Input
                    label="PowerApps URL"
                    type="text"
                    id="environmentUrl"
                    value={formData.tapUrl ?? initialFormData.tapUrl}
                    onChange={(e) => {
                      setFormData({ ...formData, tapUrl: e.target.value });
                      clearError();
                    }}
                    placeholder="Enter PowerApps URL"
                    required
                    disabled={loading || success}
                  />
                  <Input
                    label="Tenant ID"
                    type="text"
                    id="tenantId"
                    value={formData.powerappsTenantId ?? initialFormData.powerappsTenantId}
                    onChange={(e) => {
                      setFormData({ ...formData, powerappsTenantId: e.target.value });
                      clearError();
                    }}
                    placeholder="Enter tenant ID"
                    required
                    disabled={loading || success}
                    autoComplete="off"
                  />
                  <Input
                    label="Client ID"
                    type="password"
                    id="powerappsClientId"
                    value={formData.powerappsClientId ?? initialFormData.powerappsClientId}
                    onChange={(e) => {
                      setFormData({ ...formData, powerappsClientId: e.target.value });
                      clearError();
                    }}
                    placeholder="Enter client ID"
                    required
                    disabled={loading || success}
                    autoComplete="off"
                  />
                  <Input
                    label="Client Secret"
                    type="password"
                    id="powerappsClientSecret"
                    value={formData.powerappsClientSecret ?? initialFormData.powerappsClientSecret}
                    onChange={(e) => {
                      setFormData({ ...formData, powerappsClientSecret: e.target.value });
                      clearError();
                    }}
                    placeholder="Enter client secret"
                    required
                    disabled={loading || success}
                    autoComplete="off"
                  />
                </>
              ) : isSalesforce ? (
                <>
                  <Input
                    label="Instance URL"
                    type="text"
                    id="salesforceInstanceUrl"
                    value={formData.tapUrl ?? initialFormData.tapUrl}
                    onChange={(e) => {
                      setFormData({ ...formData, tapUrl: e.target.value });
                      clearError();
                    }}
                    placeholder="Enter Salesforce instance URL"
                    required
                    disabled={loading || success}
                  />
                  <Input
                    label="Client ID"
                    type="password"
                    id="salesforceClientId"
                    value={formData.salesforceClientId ?? initialFormData.salesforceClientId}
                    onChange={(e) => {
                      setFormData({ ...formData, salesforceClientId: e.target.value });
                      clearError();
                    }}
                    placeholder="Enter client ID"
                    required
                    disabled={loading || success}
                    autoComplete="off"
                  />
                  <Input
                    label="Client Secret"
                    type="password"
                    id="salesforceClientSecret"
                    value={formData.salesforceClientSecret ?? initialFormData.salesforceClientSecret}
                    onChange={(e) => {
                      setFormData({ ...formData, salesforceClientSecret: e.target.value });
                      clearError();
                    }}
                    placeholder="Enter client secret"
                    required
                    disabled={loading || success}
                    autoComplete="off"
                  />
                </>
              ) : (
                <>
                  <Input
                    label="TAP URL"
                    type="text"
                    id="tapUrl"
                    value={formData.tapUrl ?? initialFormData.tapUrl}
                    onChange={(e) => {
                      setFormData({ ...formData, tapUrl: e.target.value });
                      clearError();
                    }}
                    placeholder="Enter TAP URL"
                    required
                    disabled={loading || success}
                  />
                  <Input
                    label="Client ID"
                    type="password"
                    id="clientId"
                    value={formData.clientId ?? initialFormData.clientId}
                    onChange={(e) => {
                      setFormData({ ...formData, clientId: e.target.value });
                      clearError();
                    }}
                    placeholder="Enter client ID"
                    required
                    disabled={loading || success}
                    autoComplete="off"
                  />
                  <Input
                    label="Client Secret"
                    type="password"
                    id="clientSecret"
                    value={formData.clientSecret ?? initialFormData.clientSecret}
                    onChange={(e) => {
                      setFormData({ ...formData, clientSecret: e.target.value });
                      clearError();
                    }}
                    placeholder="Enter client secret"
                    required
                    disabled={loading || success}
                    autoComplete="off"
                  />
                </>
              )}
            </div>
          </div>
          {/* Bottom Section */}
          <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
            <Button
              label="Cancel"
              type="button"
              onClick={handleClose}
              variant="outline"
              disabled={loading}
            />
            <Button
              label={loading 
                ? (isUpdating ? 'Updating...' : 'Linking...') 
                : (isUpdating ? 'Update' : 'Link')
              }
              type="submit"
              variant="primary"
              disabled={isLinkButtonDisabled()}
              loading={loading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};
